import React from 'react'
import { Progress } from '../../components/progress'
import { UITypography } from './ui-typography'
import { cn } from '../../lib/utils'
import { type VariantProps } from 'class-variance-authority'
import { UIBox } from '../layout/ui-box'

export interface UIProgressProps extends React.ComponentProps<typeof Progress> {
  /**
   * 진행률 라벨 표시 여부
   */
  showLabel?: boolean
  /**
   * 사용자 정의 라벨
   */
  label?: string
  /**
   * 진행률 값 표시 여부
   */
  showValue?: boolean
  /**
   * 진행률 값 형식
   */
  valueFormat?: 'percent' | 'fraction'
  /**
   * 최대값
   */
  max?: number
  /**
   * 컨테이너 클래스명
   */
  containerClassName?: string
  /**
   * 애니메이션 지속 시간 (ms)
   */
  animationDuration?: number
}

/**
 * 프로그래스 컴포넌트
 * 라벨, 값 표시, 사용자 정의 형식을 지원합니다.
 *
 * @param value - 진행률 값. 0과 `max`(기본 100) 사이의 숫자여야 합니다.
 *   범위를 벗어나는 값은 자동으로 0~max 범위로 클램핑(clamping)됩니다.
 * @param max - 진행률의 최대값 (기본값: 100). value는 0~max 범위로 정규화됩니다.
 */
export const UIProgress = React.forwardRef<
  React.ComponentRef<typeof Progress>,
  UIProgressProps
>(({
  className,
  showLabel = false,
  label,
  showValue = false,
  valueFormat = 'percent',
  max = 100,
  value = 0,
  containerClassName,
  animationDuration = 500,
  ...props
}, ref) => {
  const normalizedValue = Math.min(Math.max(value ?? 0, 0), max)
  const percentage = (normalizedValue / max) * 100

  const formatValue = () => {
    if (valueFormat === 'percent') {
      return `${Math.round(percentage)}%`
    }
    return `${normalizedValue}/${max}`
  }

  const displayLabel = label || `진행률: ${formatValue()}`

  if (!showLabel && !showValue) {
    return (
      <Progress
        ref={ref}
        value={percentage}
        className={className}
        style={{
          ['--animation-duration' as string]: `${animationDuration}ms`
        }}
        {...props}
      />
    )
  }

  return (
    <UIBox className={cn("space-y-2", containerClassName)}>
      {(showLabel || showValue) && (
        <UIBox className="flex items-center justify-between">
          {showLabel && (
            <UITypography variant="body2" weight="medium">
              {label || "진행률"}
            </UITypography>
          )}
          {showValue && (
            <UITypography variant="body2" color="muted">
              {formatValue()}
            </UITypography>
          )}
        </UIBox>
      )}

      <Progress
        ref={ref}
        value={percentage}
        className={className}
        style={{
          ['--animation-duration' as string]: `${animationDuration}ms`
        }}
        {...props}
      />
    </UIBox>
  )
})

// 특정 용도별 프로그래스 컴포넌트들
export const UILoadingProgress = React.forwardRef<
  React.ComponentRef<typeof UIProgress>,
  Omit<UIProgressProps, 'variant'> & {
    status?: 'loading' | 'success' | 'error'
  }
>(({ status = 'loading', ...props }, ref) => {
  const statusConfig = {
    loading: { variant: 'default' as const, label: '로딩 중...' },
    success: { variant: 'default' as const, label: '완료' },
    error: { variant: 'default' as const, label: '오류 발생' }
  }

  const config = statusConfig[status]

  return (
    <UIProgress
      ref={ref}
      variant={config.variant}
      label={props.label || config.label}
      showLabel
      showValue
      {...props}
    />
  )
})

export const UIFileUploadProgress = React.forwardRef<
  React.ComponentRef<typeof UIProgress>,
  Omit<UIProgressProps, 'variant'> & {
    fileName?: string
    fileSize?: string
  }
>(({ fileName, fileSize, ...props }, ref) => {
  const label = fileName
    ? `${fileName}${fileSize ? ` (${fileSize})` : ''} 업로드`
    : '파일 업로드'

  return (
    <UIProgress
      ref={ref}
      variant="default"
      label={label}
      showLabel
      showValue
      valueFormat="percent"
      {...props}
    />
  )
})

UIProgress.displayName = "UIProgress"
UILoadingProgress.displayName = "UILoadingProgress"
UIFileUploadProgress.displayName = "UIFileUploadProgress"